import { useContext } from "react";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";

import {
  Box,
  Grid,
  Button,
  Typography,
  Avatar,
  Paper,
  Tooltip,
  useTheme
} from "@mui/material";

import CreditCardIcon from "@mui/icons-material/CreditCard";
import WarningIcon from "@mui/icons-material/Warning";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faReceipt,
  faLeaf
} from "@fortawesome/free-solid-svg-icons";

import { formatDate, formatDollars } from "@aclymatepackages/formatters";

import CreditCardSlider from "../../inputs/CreditCardSlider";
import BillingMethodCardDisplay from "./BillingMethodCardDisplay";
import StripeElements from "../../modules/StripeElements";

import FeatureErrorBoundary from "../../atoms/FeatureErrorBoundary";
import Card from "../../atoms/mui/Card";
import Link from "../../atoms/mui/Link";

import InvoicesTable from "./InvoicesTable";
import { makeColumnObj } from "../../modules/tables/SortableTable";

import {
  calculateIsCardExpiringSoon,
  useSubscriptionProducts
} from "../../../helpers/components/settings";
import { useCustomerInvoices } from "../../../helpers/hooks/stripe";
import { useSubscriptionType } from "../../../helpers/hooks/companyData";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import { StripeCustomerContext } from "../../../helpers/contexts/stripeCustomer";
import {
  useCachedDisplayData,
  useAccountData
} from "../../../helpers/firebase";
import usePermissions from "../../../helpers/hooks/usePermissions";

const PaymentMethodCard = () => {
  const history = useHistory();
  const { action } = useParams();
  const { isAdmin } = usePermissions();

  const { paymentMethod, isPaymentMethodLoading } = useContext(
    StripeCustomerContext
  );
  const { card, us_bank_account } = paymentMethod || {};
  const { expiration, expirationDate } = card || {};

  const isCardExpiringSoon = calculateIsCardExpiringSoon(
    expirationDate,
    expiration
  );

  const billingSubtitle = () => {
    if (!card && !us_bank_account) {
      return "Your credit card or US bank account needs to be setup.";
    }

    if (card && isCardExpiringSoon) {
      return "Your credit card is expiring soon. Click on the credit card below to edit it.";
    }

    if (card) {
      return "Your credit card is setup. Click on the credit card if you'd like to edit your billing information.";
    }

    return "Your US bank account is setup. Click on the bank card if you'd like to edit your billing information.";
  };

  return (
    <>
      {action === "payment-method" && (
        <StripeElements>
          <CreditCardSlider
            setSliderOpen={() =>
              history.push("/platform/company/settings/billing")
            }
          />
        </StripeElements>
      )}
      <Card
        cardType="layout"
        gridProps={{ sm: 6 }}
        isLoading={isPaymentMethodLoading}
        icon={<CreditCardIcon />}
        title="Billing"
        subtitle={billingSubtitle()}
        action={card && isCardExpiringSoon && <WarningIcon color="error" />}
        content={
          <Grid container justifyContent="center">
            <BillingMethodCardDisplay
              onClick={
                isAdmin
                  ? () =>
                      history.push(
                        "/platform/company/settings/billing/payment-method"
                      )
                  : undefined
              }
              noCardComponent={
                isAdmin && (
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() =>
                        history.push(
                          "/platform/company/settings/billing/payment-method"
                        )
                      }
                    >
                      Add your payment information
                    </Button>
                  </Grid>
                )
              }
            />
          </Grid>
        }
      />
    </>
  );
};

const SubscriptionDisplayCard = ({
  type: existingType,
  interval: existingInterval,
  upcomingInvoice,
  status
}) => {
  const { palette } = useTheme();
  const [subscriptionProducts] = useSubscriptionProducts();
  const { title, prices, icon } = subscriptionProducts.find(
    (product) => product.product === existingType
  );
  const { value: price } = prices.find(
    (price) => price.interval === existingInterval
  );

  const { dollarAmountDue, dueDate } = upcomingInvoice || {};

  const buildSubtitle = () => {
    if (status === "incomplete") {
      return "The payment for this subscription is still processing.";
    }

    if (status === "canceled") {
      return `This subscription is canceled and won't renew on ${formatDate(
        dueDate
      )}`;
    }

    return `Next payment of ${formatDollars(
      dollarAmountDue
    )} due on ${formatDate(dueDate)}`;
  };

  return (
    <Grid item>
      <Paper>
        <Box p={1} style={{ backgroundColor: palette.backgroundGray.light }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <Avatar>
                <FontAwesomeIcon icon={icon} />
              </Avatar>
            </Grid>
            <Grid item>
              <Typography
                variant="subtitle2"
                color="textPrimary"
              >{`${title}- ${formatDollars(
                price
              )} ${existingInterval}ly`}</Typography>
              <Typography variant="caption" color="textSecondary">
                {buildSubtitle()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Grid>
  );
};
SubscriptionDisplayCard.propTypes = {
  type: PropTypes.string,
  interval: PropTypes.string,
  upcomingInvoice: PropTypes.shape({
    dollarAmountDue: PropTypes.number,
    dueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
  }),
  status: PropTypes.string
};

const SubscriptionsCard = () => {
  const { openCommunicationCenter } = usePlatformLayoutContext();
  const [subscription, subscriptionLoading] = useSubscriptionType("saas");
  const { status, type, isPaid, cancelAtPeriodEnd, trialDate } = subscription;

  const buildSubtitle = () => {
    if (subscriptionLoading) {
      return "Loading...";
    }

    if (status === "active" || (status === "canceled" && cancelAtPeriodEnd)) {
      return `Your ${type} subscription is currently active`;
    }

    if (!status || status === "canceled") {
      return "You have no active subscriptions. Check out our subscription products by clicking on the button below.";
    }

    if (status === "trialing") {
      return `You're currently on a trial subscription that expires on  ${formatDate(
        trialDate
      )}`;
    }

    return `There's currently an issue with your ${type} subscription`;
  };

  const isTrialing = status === "trialing";

  return (
    <Card
      cardType="layout"
      gridProps={{ sm: 6 }}
      style={{ height: "100%" }}
      isLoading={subscriptionLoading}
      icon={<FontAwesomeIcon icon={faCalendarCheck} />}
      title="Subscriptions"
      subtitle={buildSubtitle()}
    >
      <Box style={{ height: "100%" }} display="flex" flexDirection="column">
        {isPaid && (
          <Box flexGrow={1} display="flex" alignItems="center">
            <Grid container justifyContent="center" spacing={1} wrap="wrap">
              <SubscriptionDisplayCard {...subscription} />
            </Grid>
          </Box>
        )}
        <Box
          flexGrow={!isTrialing ? 0 : 1}
          p={2}
          display="flex"
          alignItems="center"
        >
          <Grid container justifyContent="center" spacing={1}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={() => openCommunicationCenter("sales")}
              >
                Talk to Sales
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Card>
  );
};

const InvoicesCard = () => {
  const { isAdmin } = usePermissions();
  const [{ cnaught }] = useAccountData();
  const { hostedImpactPageUrl } = cnaught || {};

  const [subscriptionProducts, subscriptionProductsLoading] =
    useSubscriptionProducts();
  const [invoices, invoicesLoading] = useCustomerInvoices();
  const [offsets, offsetsLoading] = useCachedDisplayData("offsets");
  const { viewMode } = usePlatformLayoutContext();

  if (!isAdmin) {
    return null;
  }

  const componentLoading =
    invoicesLoading || subscriptionProductsLoading || offsetsLoading;

  const formatInvoices = () => {
    const offsetsWithoutInvoices = offsets.filter(
      ({ hostedInvoiceUrl }) => !hostedInvoiceUrl
    );

    if (
      componentLoading ||
      viewMode === "sandbox" ||
      (!invoices.length && !offsetsWithoutInvoices.length)
    ) {
      return [];
    }

    const formattedInvoices = invoices.map(({ type, ...otherProps }) => {
      const { icon } = subscriptionProducts.find(
        (product) => product.product === type
      ) || { icon: faLeaf };
      return { icon, type, ...otherProps };
    });
    const formattedOffsetsWithoutInvoices = offsetsWithoutInvoices.map(
      ({ date, name, totalOffsetCost }) => ({
        type: "offset",
        icon: faLeaf,
        date,
        name,
        dollarValue: totalOffsetCost
      })
    );

    return [...formattedInvoices, ...formattedOffsetsWithoutInvoices];
  };

  const columns = [
    makeColumnObj("TYPE", "type", true),
    makeColumnObj("DATE", "date", true),
    makeColumnObj("NAME", "name", true),
    makeColumnObj("PRICE", "dollarValue", true),
    makeColumnObj("ATTRIBUTIONS", "attributions"),
    makeColumnObj("INVOICE")
  ];

  const formattedInvoices = formatInvoices();

  const adjustedInvoicesWithAttributions = formattedInvoices.map((invoice) => {
    const { invoiceId } = invoice || {};

    const matchedOffset = offsets.find(
      ({ stripeInvoiceId }) => stripeInvoiceId === invoiceId
    );

    if (!matchedOffset) {
      return invoice;
    }

    const {
      badgeYears = [],
      events = [],
      attributionYear
    } = matchedOffset || {};

    const badgesAttr = !badgeYears.length
      ? []
      : badgeYears.map((badgeYear) => {
          return {
            type: "badge",
            name: badgeYear.badge,
            value: badgeYear.year
          };
        });

    const eventsAttr = !events.length
      ? []
      : events.map((event) => ({
          type: "event",
          name: event.name,
          value: event.name
        }));

    const attributions = [
      ...badgesAttr,
      ...eventsAttr,
      { type: "year", value: attributionYear }
    ];

    return { ...invoice, attributions };
  });

  return (
    <Card
      cardType="layout"
      gridProps={{ sm: 12 }}
      isLoading={invoicesLoading}
      title="Invoices"
      icon={<FontAwesomeIcon icon={faReceipt} />}
      action={
        hostedImpactPageUrl && (
          <Tooltip title="See the impact from the offsets you've purchased">
            <span>
              <Link href={hostedImpactPageUrl} target="_blank" rel="noopener">
                <Button
                  variant="contained"
                  color="secondary"
                  endIcon={<OpenInNewIcon />}
                  size="small"
                >
                  View your CNaught Impact Page
                </Button>
              </Link>
            </span>
          </Tooltip>
        )
      }
    >
      <InvoicesTable
        formattedInvoices={adjustedInvoicesWithAttributions}
        columns={columns}
      />
    </Card>
  );
};

const Billing = () => {
  return (
    <FeatureErrorBoundary featureName="Billing">
      <Grid container spacing={2} alignItems="stretch">
        <PaymentMethodCard />
        <SubscriptionsCard />
        <InvoicesCard />
      </Grid>
    </FeatureErrorBoundary>
  );
};
export default Billing;
